import { Box, Grid, GridItem, Stack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDataLoader from '@/components/Atoms/FaceliftDataLoader';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftOverlayLoader from '@/components/Atoms/FaceliftOverlayLoader';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftDragDrop from '@/components/Molecules/FaceliftDragDrop';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import { FilePreviewList } from '@/components/Molecules/FilePreviewList';
import Show from '@/components/Molecules/Show';
import { DRAFT_TYPE_LABELS, FORM_TYPES } from '@/constants';
import { type UploadedFile, useFileUpload } from '@/hooks';
import {
  BgProcessModule,
	useCreateBgProcess,
	useUpdateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useSaveDraft } from '@/hooks/service-hooks/drafts';
import {
	useGetSupportTicket,
} from '@/hooks/service-hooks/supportTickets';
import type {
	CreateSupportTicketPayload,
	UpdateSupportTicketPayload,
} from '@/services/supportTickets';
import { useAuthStore, useClinicStore } from '@/store';
import type { Draft } from '@/types/draft';
import {
	extractDocumentName,
	mapPriority,
	mapStatus,
	priorityOptions,
	statusOptions,
	typeOptions,
} from '../constants';
import type { SupportTicketFormProps } from './types';

/**
 * Form validation schema using Yup
 * Defines required fields and validation rules for the support ticket form
 */
const supportTicketSchema = yup.object({
	subject: yup.string().trim().required('Subject is required'),
	description: yup.string().trim().default(''),
	clinic: yup.mixed<SelectItem>().defined().required('Clinic is required'),
	priority: yup.mixed<SelectItem>().defined().required('Priority is required'),
	type: yup.mixed<SelectItem>().defined().required('Type is required'),
	status: yup.mixed<SelectItem>().defined().required('Status is required'),
});

type FormData = yup.InferType<typeof supportTicketSchema>;

const initialDefaultValues: Partial<FormData> = {
	subject: '',
	description: '',
	clinic: undefined,
	priority: undefined,
	type: undefined,
	status: undefined,
};

/**
 * SupportTicketForm Component
 *
 * A form component for creating and editing support tickets.
 *
 * Features:
 * - Create new support tickets or edit existing ones
 * - Multiple file uploads to SharePoint
 * - Auto-population of form fields in edit mode
 * - File management without delete capability
 * - Last uploaded file is saved to the ticket
 *
 * @param mode - 'create' or 'edit' mode
 * @param ticketId - ID of the ticket to edit (required in edit mode)
 * @param onSuccess - Callback function called after successful form submission
 */
const SupportTicketForm: React.FC<SupportTicketFormProps> = ({
	mode,
	ticketId,
	existingDraft,
	onSuccess,
	initialData,
	onIsDirtyChange,
	onExitConfirmationDraftActionChange,
}) => {
	const hasExistingSupportTicket = mode === 'edit' && Boolean(ticketId);

	// Get available clinics from the global store
	const clinicsFromStore = useClinicStore((state) => state.currentClinics);

	// Get current authenticated user information
	const user = useAuthStore((state) => state.user);
  const userId = user?.userInformation?._id || null;
	const userName = user?.userInformation ? `${user.userInformation.firstName} ${user.userInformation.lastName}` : '';

	// Fetch ticket details only in edit mode
	const { data: ticketDetails, isFetching: isFetchingDetails } =
		useGetSupportTicket(
			hasExistingSupportTicket ? ticketId || null : null,
			hasExistingSupportTicket,
		);

	// Transform clinics array into SelectItem format for dropdown
	const clinicOptions = useMemo(
		() =>
			clinicsFromStore.map((clinic) => ({
				value: clinic.accountid,
				label: clinic.name,
			})),
		[clinicsFromStore],
	);

	const {
		mutateAsync: createTicket,
		isLoading: createLoading,
		isSuccess: createSuccess,
		isError: createError,
	} = useCreateBgProcess(() => {
		setTimeout(() => {
			onSuccess({ action: 'submitted' });
		}, 2000);
	});

	const {
		mutateAsync: updateTicket,
		isLoading: updateLoading,
		isSuccess: updateSuccess,
		isError: updateError,
	} = useUpdateBgProcess(() => {
		setTimeout(() => {
			onSuccess({ action: 'submitted' });
		}, 2000);
	});

	// Draft save mutation
	const { mutate: saveDraft, isLoading: isSavingDraft, isEditingDraft } =
		useSaveDraft({
			existingDraft,
			onSuccess: () => {
				toast.success(
					existingDraft
						? 'Draft updated successfully'
						: 'Draft saved successfully',
				);
				onSuccess({ action: 'draft_saved' });
			},
		});

	/**
	 * React Hook Form setup with Yup validation
	 * Manages form state, validation, and submission
	 */
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
	} = useForm<FormData>({
		resolver: yupResolver(supportTicketSchema),
		mode: 'onChange',
		defaultValues: initialDefaultValues,
	});

	// Local state to manage uploaded files
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	
	// Track which fields were auto-populated and should be disabled
	const [disabledFields, setDisabledFields] = useState({
		clinic: false,
		type: false,
		subject: false,
		status: false,
	});

	const hasInitializedRef = useRef(false);
	const prevModeRef = useRef(mode);

	/**
	 * File upload hook configuration
	 * Handles uploading files to SharePoint in the 'Support' folder
	 * Files are uploaded to the selected clinic's folder
	 */
	const { handleFileUpload, formatFileSize, uploadError, handleDeleteFile, isUploading, isDisabled, isDeleting } =
		useFileUpload({
			files: uploadedFiles,
			onFilesChange: (files: UploadedFile[]) => {
				setUploadedFiles(files);
			},
			clinic: hasExistingSupportTicket
				? ((watch('clinic') as SelectItem)?.value as unknown as string[])
						?.at(0)
						?.replaceAll('-', '') || ''
				: String(watch('clinic')?.value || '').replaceAll('-', ''),
			clinicName: String(watch('clinic')?.label || ''),
			folderName: `Client Inbox/Support/${watch('subject')}`,
			disabled: !watch('clinic')?.value || !watch('subject'), // Disable if clinic is not selected
		});

	/**
	 * Effect hook to populate form fields and load files
	 *
	 * In EDIT mode:
	 * - Populates all form fields with existing ticket data
	 * - Loads the existing uploaded file (if any) from SharePoint URL
	 *
	 * In CREATE mode:
	 * - Can load from an existing draft
	 * - Resets the form to empty state or initial data
	 * - Clears any uploaded files
	 */
	useEffect(() => {
		// Load draft data (only when existingDraft is provided)
		if (existingDraft && !hasExistingSupportTicket) {
			try {
				reset(existingDraft.formData as FormData, { keepDefaultValues: false });
				// Also restore uploaded files if they exist in draft
				if (existingDraft.formData.documents) {
					setUploadedFiles(existingDraft.formData.documents as UploadedFile[]);
				}
			} catch (error) {
				console.error('Failed to load draft data:', error);
			}
			return; // Exit early if loading from draft
		}

		if (hasExistingSupportTicket && ticketDetails) {
			// Map numeric priority and status codes to their display labels
			const priority = mapPriority(ticketDetails.prioritycode);
			const status = mapStatus(ticketDetails.statuscode);

			// Find the ticket type from either casetypecode or caseorigincode
			const type = typeOptions.find(
				(opt) =>
					Number(opt.value) === ticketDetails.casetypecode ||
					Number(opt.value) === ticketDetails.caseorigincode,
			);

			// Use clinic data directly from the API response
			// No need to search through available clinics - the response already has the clinic info
			const customerId = ticketDetails._customerid_value || ticketDetails.customer?.accountid;
			const customerName = ticketDetails.customer?.name || ticketDetails.customerName;
			
			const clinic = customerId && customerName 
				? { value: customerId, label: customerName }
				: undefined;

			// Populate all form fields with ticket data
			reset({
				subject: ticketDetails.title || '',
				description: ticketDetails.description || '',
				clinic: clinic || undefined,
				priority: priority
					? { value: String(ticketDetails.prioritycode), label: priority.label }
					: undefined,
				type: type || undefined,
				status: status
					? { value: String(ticketDetails.statuscode), label: status.label }
					: undefined,
			}, { keepDefaultValues: false });

			// Load existing document from SharePoint URL if available
			if (ticketDetails.ta_documentupload) {
				const documentName = extractDocumentName(
					ticketDetails.ta_documentupload,
				);

				// Check if we have a valid filename (not just a folder path)
				if (documentName && documentName !== '---') {
					// Clean up the URL (remove trailing slashes)
					const cleanUrl = ticketDetails.ta_documentupload.endsWith('/')
						? ticketDetails.ta_documentupload.slice(0, -1)
						: ticketDetails.ta_documentupload;

					// Construct the full file URL
					const fullFileUrl = cleanUrl.endsWith(documentName)
						? cleanUrl
						: `${cleanUrl}/${documentName}`;

					// Create a file object to display in the file preview list
					setUploadedFiles([
						{
							id: 'existing-doc',
							name: documentName,
							size: 0,
							type: 'application/pdf',
							file: null,
							sharePointUrl: fullFileUrl,
							sharePointFileName: documentName,
							clinic: String(clinic?.value || '').replaceAll('-', ''),
							folderName: 'Client Inbox/Support',
						},
					]);
				} else {
					setUploadedFiles([]);
				}
			} else {
				setUploadedFiles([]);
			}
		} else if (mode === 'create') {
			const modeChanged = prevModeRef.current !== mode;
			if (!hasInitializedRef.current || modeChanged) {
				const defaultFormData = { ...initialDefaultValues };
				
				if (initialData) {
					if (initialData.clinic) {
						defaultFormData.clinic = initialData.clinic;
					}
					if (initialData.type) {
						defaultFormData.type = initialData.type;
					}
					if (initialData.subject) {
						defaultFormData.subject = initialData.subject;
					}
					if (initialData.status) {
						defaultFormData.status = initialData.status;
					}
				}
				
				reset(defaultFormData, { keepDefaultValues: false, keepDirty: false });
				setUploadedFiles([]);
				
				if (initialData) {
					const newDisabledFields = {
						clinic: false,
						type: false,
						subject: false,
						status: false,
					};
					
					if (initialData.clinic) {
						newDisabledFields.clinic = true;
					}
					if (initialData.type) {
						newDisabledFields.type = true;
					}
					if (initialData.subject) {
						newDisabledFields.subject = true;
					}
					if (initialData.status) {
						newDisabledFields.status = true;
					}
					
					setDisabledFields(newDisabledFields);
				} else {
					// Reset disabled fields if no initial data
					setDisabledFields({
						clinic: false,
						type: false,
						subject: false,
						status: false,
					});
				}
				
				hasInitializedRef.current = true;
			}
			prevModeRef.current = mode;
		} else {
			hasInitializedRef.current = false;
			prevModeRef.current = mode;
		}
	}, [
		existingDraft,
		hasExistingSupportTicket,
		initialData,
		mode,
		reset,
		ticketDetails,
	]);

	useEffect(() => {
		if (onIsDirtyChange) {
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty, onIsDirtyChange]);

	/**
	 * Handle save as draft
	 * Saves the current form state and uploaded files to drafts
	 */
	const handleSaveAsDraft = useCallback(() => {
		const currentFormData = watch();
		
		// Generate title from subject or clinic name
		const titleOrReference = currentFormData.subject || currentFormData.clinic?.label || 'Untitled Draft';
		
		// Include uploaded files in the draft
		const formDataWithFiles = {
			...currentFormData,
			documents: uploadedFiles,
		};
		
		saveDraft({
			userId: userId || '',
			userName,
			formType: FORM_TYPES.SUPPORT_TICKET,
			draftType: DRAFT_TYPE_LABELS.SUPPORT_TICKET,
			titleOrReference,
			formData: formDataWithFiles,
		});
	}, [saveDraft, uploadedFiles, userId, userName, watch]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (mode !== 'create') {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: isEditingDraft ? 'Update Draft' : 'Save as Draft',
			onClick: handleSaveAsDraft,
			loading: isSavingDraft,
			disabled: isSavingDraft || createLoading || updateLoading,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		handleSaveAsDraft,
		createLoading,
		isEditingDraft,
		isSavingDraft,
		mode,
		onExitConfirmationDraftActionChange,
		updateLoading,
	]);

	/**
	 * Form submission handler
	 *
	 * Handles both create and update operations:
	 * - Gets the LAST uploaded file's SharePoint URL (allows multiple uploads)
	 * - In CREATE mode: Creates a new ticket with all required fields
	 * - In EDIT mode: Updates the existing ticket with modified fields
	 *
	 * Note: The last uploaded file will be saved to ta_documentupload field
	 */
	const onFormSubmit = handleSubmit(async (data) => {
		// Get the last uploaded file's URL (if any files were uploaded)
		const documentUploadUrl =
			uploadedFiles.length > 0
				? uploadedFiles[uploadedFiles.length - 1].sharePointUrl
				: '';

		if (mode === 'create') {
			// Build payload for creating a new support ticket
			const payload: CreateSupportTicketPayload = {
				new_patientfirstname: user?.name?.split(' ')[0] || '',
				new_patientlastname: user?.name?.split(' ')[1] || '',
				emailaddress: user?.email || '',
				createdon: new Date().toISOString(),
				description: data.description || '',
				title: data.subject,
				prioritycode: Number((data.priority as SelectItem)?.value) || 2,
				statuscode: Number((data.status as SelectItem)?.value) || 1,
				casetypecode: Number((data.type as SelectItem)?.value) || 953870005,
				ta_origin: 953870004, // Static value for origin
				'customerid_account@odata.bind': `contacts(${(data.clinic as SelectItem)?.value})`,
				ta_documentupload: documentUploadUrl,
				ta_department: 953870011, // Static value for department
			};

      createTicket({
        moduleName: BgProcessModule.SUPPORT_TICKET,
        userId: userId || '',
        clinicId: (data?.clinic as SelectItem)?.value as string,
        payload: { 
          payload, 
          additionalPayload: {
            clinicName: (data?.clinic as SelectItem)?.label as string,
          } 
        },
      });
		} else {
			// Build payload for updating an existing support ticket
			const payload: UpdateSupportTicketPayload = {
				title: data.subject,
				description: data.description || '',
				prioritycode: Number((data.priority as SelectItem)?.value) || 2,
				statuscode: Number((data.status as SelectItem)?.value) || 1,
				casetypecode: Number((data.type as SelectItem)?.value) || 953870005,
				ta_documentupload: documentUploadUrl,
			};

			if (ticketId) {
          updateTicket({
            moduleName: BgProcessModule.SUPPORT_TICKET,
            userId: userId || '',
            clinicId: data.clinic && data.clinic.value ? `${((data.clinic as SelectItem)?.value as unknown as string[])?.at(0)}` : undefined,
            payload: { 
              payload, 
              additionalPayload: {
                clinicName: (data?.clinic as SelectItem)?.label as string,
              } 
            },
            crmId: ticketId || '',
          })
			}
		}
	});

	// Aggregate loading, success, and error states from both mutations
	const isLoading = createLoading || updateLoading;
	const isSuccess = createSuccess || updateSuccess;
	const isError = createError || updateError;

	/**
	 * Show loading state while fetching ticket details in edit mode
	 * Only shown during initial data fetch, not during form submission
	 */
	if (isFetchingDetails && !isLoading && !isSuccess) {
		return (
			<Box
				px={6}
				py={4}
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="400px"
			>
				<FaceliftDataLoader
					title="Loading ticket details..."
					description="Please wait while we fetch the ticket information."
				/>
			</Box>
		);
	}

	return (
		<>
			<Box px={6} py={4}>
				{/* Show overlay loader during file upload */}
				<Show when={isUploading || isDeleting}>
					<FaceliftOverlayLoader />
				</Show>

				{/* 
        MAIN FORM LAYOUT
        3-column responsive grid for better balance
        Row 1: Subject (span 2) | Priority
        Row 2: Clinic (span 2) | Type
        Row 3: Description (span 2) | Status
      */}
				<Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
				{/* Subject Field - spans 2 columns */}
				<GridItem colSpan={2}>
					<FaceliftInput
						topLabel="Subject"
						placeholder="Type Customer Name"
						{...register('subject')}
						error={errors.subject?.message}
						required
						disabled={disabledFields.subject}
					/>
				</GridItem>

					{/* Priority Field */}
					<GridItem>
						<FaceliftSelect<SelectItem, false>
							topLabel="Priority"
							placeholder="Select"
							value={watch('priority') || null}
							onChange={(newValue) => {
								if (newValue) setValue('priority', newValue, { shouldValidate: true, shouldDirty: true });
							}}
							items={priorityOptions}
							error={
								typeof errors.priority?.message === 'string'
									? errors.priority.message
									: ''
							}
							isSearchable={false}
							required
						/>
					</GridItem>

				{/* Clinic Field - spans 2 columns */}
				<GridItem colSpan={2}>
					<FaceliftSelect<SelectItem, false>
						topLabel="Clinic"
						placeholder="Select Clinic"
						value={watch('clinic') || null}
						onChange={(newValue) => {
							if (newValue) setValue('clinic', newValue, { shouldValidate: true, shouldDirty: true });
						}}
						items={clinicOptions}
						error={
							typeof errors.clinic?.message === 'string'
								? errors.clinic.message
								: ''
						}
						isSearchable={true}
						required
						isDisabled={mode === 'edit' || disabledFields.clinic}
					/>
				</GridItem>

				{/* Type Field */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						topLabel="Type"
						placeholder="Select"
						value={watch('type') || null}
						onChange={(newValue) => {
							if (newValue) setValue('type', newValue, { shouldValidate: true, shouldDirty: true });
						}}
						items={typeOptions}
						error={
							typeof errors.type?.message === 'string'
								? errors.type.message
								: ''
						}
						isSearchable={false}
						required
						isDisabled={mode === 'edit' || disabledFields.type}
					/>
				</GridItem>

					{/* Description Field - spans 2 columns */}
					<GridItem colSpan={2}>
						<FaceliftInput
							topLabel="Description"
							placeholder="Description"
							{...register('description')}
							error={errors.description?.message}
						/>
					</GridItem>

				{/* Status Field */}
				<GridItem>
					<FaceliftSelect<SelectItem, false>
						topLabel="Status"
						placeholder="Select"
						value={watch('status') || null}
						onChange={(newValue) => {
							if (newValue) setValue('status', newValue, { shouldValidate: true, shouldDirty: true });
						}}
						items={statusOptions}
						error={
							typeof errors.status?.message === 'string'
								? errors.status.message
								: ''
						}
						isSearchable={false}
						required
						isDisabled={disabledFields.status}
					/>
				</GridItem>
				</Grid>

				{/* 
        FILE UPLOAD SECTION
        - Allows multiple file uploads
        - Files are uploaded to SharePoint
        - No delete option (files cannot be removed once uploaded)
        - Displays list of uploaded files with name and size
      */}
				<Box mb={4}>
					<FaceliftText
						fontSize="sm"
						fontWeight="semibold"
						mb={2}
						fontStyle="italic"
					>
						Upload Documents
					</FaceliftText>
					{/* Drag and drop file uploader */}
					<FaceliftDragDrop
						onFilesUpload={handleFileUpload}
						error={uploadError}
						multiple={true}
						maxSize={104857600} // 100MB max file size
						disabled={isDisabled}
						disabledMessage="Please select a clinic and enter a subject before uploading files"
					/>
					{/* Display uploaded files (without delete option) */}
					<FilePreviewList
						files={uploadedFiles}
						formatFileSize={formatFileSize}
						onDelete={handleDeleteFile}
					/>
				</Box>

				{/* Submit/Update Button */}
				<Stack direction="row" justify="flex-end" gap={3}>
					{mode === 'create' && (
						<FaceliftButton
							onClick={handleSaveAsDraft}
							type="button"
							width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
							disabled={isSavingDraft || isLoading}
							bg="transparent"
							color="brand.600"
							border="1px solid"
							borderColor="brand.600"
							_hover={{
								bg: 'brand.50',
							}}
							_dark={{
								_hover: {
									bg: 'whiteAlpha.100',
								},
							}}
						>
							{isEditingDraft ? 'Update Draft' : 'Save as Draft'}
						</FaceliftButton>
					)}
					<FaceliftButton
						onClick={onFormSubmit}
						bg="brand.solidGradient"
						color="white"
						width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
						disabled={isLoading}
					>
						{mode === 'create' ? 'Submit' : 'Update'}
					</FaceliftButton>
				</Stack>
			</Box>

			{/* 
      SUCCESS/ERROR MODAL
      Shows progress and result of form submission
      - Loading state while submitting
      - Success message after successful submission
      - Error message if submission fails
    */}
			<FaceliftSuccessProgressModal
				open={isLoading || isSuccess || isError}
				isLoading={isLoading}
				isSucceed={isSuccess}
				hasError={isError}
				onOpenChange={() => {}}
				title={
					hasExistingSupportTicket
						? 'Support ticket updated successfully.'
						: 'Support ticket created successfully.'
				}
				errorMessage={
					isError
						? hasExistingSupportTicket
							? 'Error updating support ticket'
							: 'Error creating support ticket'
						: ''
				}
			/>
		</>
	);
};

export default SupportTicketForm;
